import React, { useState, useEffect, useCallback } from 'react';
import { usersApi } from '../api/rangesApi';
import '../styles/AdminPanel.css';

function AdminPanel({ onClose }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Форма нового пользователя
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    is_admin: false,
    read_only: false,
    copy_from_user_id: ''
  });
  
  // Редактирование пароля
  const [editingUserId, setEditingUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const showNotification = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Подготавливаем данные - убираем пустой copy_from_user_id
      const userData = {
        username: formData.username,
        password: formData.password,
        is_admin: formData.is_admin,
        read_only: formData.read_only,
        ...(formData.copy_from_user_id && { copy_from_user_id: formData.copy_from_user_id })
      };
      
      await usersApi.create(userData);
      
      const message = formData.copy_from_user_id 
        ? 'Пользователь создан, диапазоны скопированы'
        : 'Пользователь создан';
      showNotification(message);
      setShowForm(false);
      setFormData({ username: '', password: '', is_admin: false, read_only: false, copy_from_user_id: '' });
      await loadUsers();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      await usersApi.update(user.id, { is_admin: !user.is_admin });
      showNotification('Права обновлены');
      await loadUsers();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleToggleReadOnly = async (user) => {
    try {
      await usersApi.update(user.id, { read_only: !user.read_only });
      showNotification('Права обновлены');
      await loadUsers();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleChangePassword = async (userId) => {
    if (!newPassword.trim()) {
      showNotification('Введите новый пароль', 'error');
      return;
    }
    try {
      await usersApi.update(userId, { password: newPassword });
      showNotification('Пароль изменён');
      setEditingUserId(null);
      setNewPassword('');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Удалить пользователя "${user.username}"? Все его диапазоны будут удалены!`)) {
      return;
    }
    try {
      await usersApi.delete(user.id);
      showNotification('Пользователь удалён');
      await loadUsers();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2>👥 Управление пользователями</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="admin-loading">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2>👥 Управление пользователями</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>👥 Управление пользователями</h2>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      {message.text && (
        <div className={`admin-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="admin-actions">
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Отмена' : '+ Новый пользователь'}
        </button>
      </div>

      {showForm && (
        <form className="user-form" onSubmit={handleCreateUser}>
          <div className="form-row">
            <input
              type="text"
              placeholder="Имя пользователя"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
            <input
              type="password"
              placeholder="Пароль"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="copy-from-select">
              <label>📋 Скопировать диапазоны от:</label>
              <select
                value={formData.copy_from_user_id}
                onChange={(e) => setFormData({ ...formData, copy_from_user_id: e.target.value })}
              >
                <option value="">— Не копировать —</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row checkboxes">
            <label>
              <input
                type="checkbox"
                checked={formData.is_admin}
                onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
              />
              Администратор
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.read_only}
                onChange={(e) => setFormData({ ...formData, read_only: e.target.checked })}
              />
              Только чтение
            </label>
          </div>
          <button type="submit" className="btn btn-success">
            Создать
          </button>
        </form>
      )}

      <div className="users-list">
        {users.map(user => (
          <div key={user.id} className="user-item">
            <div className="user-info">
              <span className="user-name">
                {user.username}
                {user.is_admin && <span className="badge admin">Админ</span>}
                {user.read_only && <span className="badge readonly">Только чтение</span>}
              </span>
              <span className="user-date">
                Создан: {new Date(user.created_at).toLocaleDateString('ru-RU')}
              </span>
            </div>
            
            <div className="user-controls">
              {editingUserId === user.id ? (
                <div className="password-edit">
                  <input
                    type="password"
                    placeholder="Новый пароль"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button 
                    className="btn btn-small btn-success"
                    onClick={() => handleChangePassword(user.id)}
                  >
                    ✓
                  </button>
                  <button 
                    className="btn btn-small btn-secondary"
                    onClick={() => { setEditingUserId(null); setNewPassword(''); }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <button
                    className="btn btn-small"
                    onClick={() => setEditingUserId(user.id)}
                    title="Изменить пароль"
                  >
                    🔑
                  </button>
                  <button
                    className={`btn btn-small ${user.is_admin ? 'btn-active' : ''}`}
                    onClick={() => handleToggleAdmin(user)}
                    title={user.is_admin ? 'Убрать права админа' : 'Сделать админом'}
                  >
                    👑
                  </button>
                  <button
                    className={`btn btn-small ${user.read_only ? 'btn-active' : ''}`}
                    onClick={() => handleToggleReadOnly(user)}
                    title={user.read_only ? 'Разрешить редактирование' : 'Только чтение'}
                  >
                    👁
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeleteUser(user)}
                    title="Удалить пользователя"
                  >
                    🗑
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminPanel;

